///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthHistoryService } from '../../../engine/history-service';
import { TunnelConstants } from './tunnel-view-constants';
import { TunnelMarkerView } from '../../marker/tunnel/tunnel-marker-view';
import LatLngLiteral = google.maps.LatLngLiteral;

export class TunnelOffset {
    total: number;
    offset: number;
}

export class TunnelView {
    lines: google.maps.Polyline[];
    marker: TunnelMarkerView;
    private readonly map: google.maps.Map;
    private tunnelsOffsets: TunnelOffset[];

    constructor(
        private readonly historyService: GtthHistoryService,
        private readonly id: number,
        private readonly color: string,
        private readonly nodesCoordinates: LatLngLiteral[]
    ) {
        this.map = this.historyService.map;
    }

    draw(newTunnelOffsets: TunnelOffset[]) {
        this.lines = [];
        this.tunnelsOffsets = [];

        for (let i = 0; i < this.nodesCoordinates.length - 1; i++) {
            this.tunnelsOffsets[i] = newTunnelOffsets[i];
            const line = this.createNewLine(this.nodesCoordinates[i], this.nodesCoordinates[i + 1], this.tunnelsOffsets[i]);
            line.setMap(this.map);
            this.lines.push(line);
        }

        if (!this.marker) {
            this.marker = new TunnelMarkerView(this.nodesCoordinates[this.nodesCoordinates.length - 1], this.id, this.color);
            this.marker.draw(this.map);
        }
    }

    setOffsetsAndRedraw(newTunnelOffsets: TunnelOffset[]) {
        newTunnelOffsets.forEach((newOffset: TunnelOffset, index) => {
            if (newOffset.total !== this.tunnelsOffsets[index].total || newOffset.offset !== this.tunnelsOffsets[index].offset) {
                this.tunnelsOffsets[index] = newOffset;
                this.lines[index].setPath(
                    this.calculateLinePath(this.nodesCoordinates[index], this.nodesCoordinates[index + 1], this.tunnelsOffsets[index])
                );
            }
        });
    }

    hide() {
        if (this.lines) {
            this.lines.forEach((line) => line.setMap(null));
            this.lines = undefined;
        }
        if (this.marker) {
            this.marker.hide();
            this.marker = undefined;
        }
    }

    private createNewLine(latLngLiteral: google.maps.LatLngLiteral, latLngLiteral2: google.maps.LatLngLiteral, tunnelOffset: TunnelOffset) {
        return new google.maps.Polyline({
            path: this.calculateLinePath(latLngLiteral, latLngLiteral2, tunnelOffset),
            geodesic: true,
            strokeOpacity: TunnelConstants.STROKE_OPACITY,
            strokeColor: this.color,
            strokeWeight: TunnelConstants.STROKE_WEIGHT,
            zIndex: TunnelConstants.Z_INDEX
        });
    }

    private calculateLinePath(
        node1Coords: google.maps.LatLngLiteral,
        node2Coords: google.maps.LatLngLiteral,
        tunnelOffset: TunnelOffset
    ): google.maps.LatLngLiteral[] {
        // offset should be calculated always in one direction, doesn't matter if path goes as node1 -> node2 or node2 -> node1
        const firstGreaterThanSecond = node1Coords.lng + node1Coords.lat > node2Coords.lng + node2Coords.lat;
        let latFrom = firstGreaterThanSecond ? node1Coords.lat : node2Coords.lat;
        let lngFrom = firstGreaterThanSecond ? node1Coords.lng : node2Coords.lng;
        let latTo = firstGreaterThanSecond ? node2Coords.lat : node1Coords.lat;
        let lngTo = firstGreaterThanSecond ? node2Coords.lng : node1Coords.lng;

        const latitudeDelta = (latTo - latFrom) / tunnelOffset.total;
        const longitudeDelta = (lngTo - lngFrom) / tunnelOffset.total;

        // how much we should skip
        latFrom = latFrom + latitudeDelta * (tunnelOffset.offset - 1);
        lngFrom = lngFrom + longitudeDelta * (tunnelOffset.offset - 1);
        // where is the end point
        latTo = latFrom + latitudeDelta;
        lngTo = lngFrom + longitudeDelta;

        return [
            { lat: latFrom, lng: lngFrom },
            { lat: latTo, lng: lngTo }
        ];
    }
}
